import React from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';

import { GT_COLORS } from '../groups-teams/shared/constants';
import { RoutingPerson } from '../../types/notification-rules.types';

/**
 * One field that holds the chosen people and adds more, like the To line of an
 * email: chips live inside the box, typing searches name and email, and the x on
 * a chip removes that person.
 *
 * Anyone with no email, text or call set up is marked, in the list and on their chip,
 * because naming someone nothing can reach is the mistake this page exists to
 * prevent.
 */

interface Props {
    options: RoutingPerson[];
    value: RoutingPerson[];
    ariaLabel: string;
    disabled?: boolean;
    onChange: (next: RoutingPerson[]) => void;
}

const initials = (name: string) => name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

export const PeopleField: React.FC<Props> = ({ options, value, ariaLabel, disabled, onChange }) => (
    <Autocomplete<RoutingPerson, true>
        multiple
        size="small"
        disabled={disabled}
        options={options}
        value={value}
        filterSelectedOptions
        disableCloseOnSelect
        onChange={(_, next) => onChange(next)}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.userId === v.userId}
        filterOptions={(opts, state) => {
            const q = state.inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter((o) => o.name.toLowerCase().includes(q) || (o.email ?? '').toLowerCase().includes(q));
        }}
        noOptionsText="No matching system users."
        sx={{ width: '100%', maxWidth: 560 }}
        slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 320 } } }}
        renderTags={(chosen, getTagProps) => chosen.map((person, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
                <Chip
                    key={key}
                    {...tagProps}
                    size="small"
                    label={person.name}
                    title={person.reachable ? person.email ?? person.name : 'No email, text or call set up'}
                    variant={person.reachable ? 'filled' : 'outlined'}
                    color={person.reachable ? 'default' : 'error'}
                />
            );
        })}
        renderInput={(params) => (
            <TextField
                {...params}
                placeholder={value.length ? '' : 'Search for people'}
                inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
            />
        )}
        renderOption={(props, option) => (
            <Box component="li" {...props} key={option.userId} sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Box sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: option.reachable ? GT_COLORS.primary : GT_COLORS.textDisabled,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 600,
                }}>
                    {initials(option.name)}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: '13px', fontWeight: 500 }}>{option.name}</Typography>
                    <Typography noWrap sx={{ fontSize: '11px', color: option.reachable ? GT_COLORS.textFaint : GT_COLORS.red }}>
                        {option.reachable ? (option.email ?? '') : 'No email, text or call set up'}
                    </Typography>
                </Box>
            </Box>
        )}
    />
);
